// Organic 디자인 시스템 토큰(_ds/styles.css) + 프로토타입 키프레임/인터랙션.
// 임시 페이지라 전역 CSS 대신 페이지 루트에서 <style>로 주입한다.

const GACHA_CSS = `
.ibg-root {
  --c-bg: #f5ead8;
  --c-surface: #ebddc5;
  --c-text: #201e1d;
  --c-divider: color-mix(in srgb, #201e1d 16%, transparent);

  /* 주 색상 올리브, warm(테라코타)은 보조 포인트 */
  --c-accent: #7a8a5e;
  --c-accent-100: #f0fae1;
  --c-accent-200: #e1eecc;
  --c-accent-300: #ccdbb2;
  --c-accent-400: #aebf92;
  --c-accent-500: #8fa073;
  --c-accent-600: #728157;
  --c-accent-700: #56633f;
  --c-accent-800: #3d472b;

  --c-warm: #c67139;
  --c-warm-100: #fff2eb;
  --c-warm-200: #ffe1d0;
  --c-warm-300: #ffc6a5;
  --c-warm-400: #f6a06b;
  --c-warm-500: #d67f48;
  --c-warm-600: #b2622d;
  --c-warm-700: #8c491a;
  --c-warm-800: #643312;

  --c-n-100: #f9f4ed;
  --c-n-200: #eee7db;
  --c-n-300: #dcd3c4;
  --c-n-400: #c0b6a5;
  --c-n-500: #a19786;
  --c-n-600: #82796a;
  --c-n-700: #645c50;
  --c-n-800: #474238;
  --c-n-900: #2e2b25;

  --f-heading: 'Caprasimo', serif;
  --f-body: 'Figtree', system-ui, sans-serif;

  --sh-sm: 0 1px 2px rgba(46, 43, 37, 0.14);
  --sh-md: 0 3px 10px rgba(46, 43, 37, 0.16);
  --sh-lg: 0 12px 32px rgba(46, 43, 37, 0.22);
  --radius-lg: 28px;
  --radius-md: 16px;
}
.ibg-root ::selection { background: color-mix(in srgb, var(--c-accent) 30%, transparent); }
.ibg-root button { font-family: var(--f-body); }
.ibg-root :focus { outline: none; }
.ibg-root :focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }

@keyframes ibgCrank { to { transform: rotate(360deg); } }
@keyframes ibgJiggle {
  0%, 100% { transform: none; }
  20% { transform: translateX(-1.2%) rotate(-0.8deg); }
  45% { transform: translateX(1.2%) rotate(0.7deg); }
  70% { transform: translateX(-0.8%) rotate(-0.4deg); }
}
@keyframes ibgTumble {
  0%, 100% { transform: none; }
  25% { transform: translateY(-3%) rotate(-2deg); }
  55% { transform: translateY(2.5%) rotate(1.6deg); }
  80% { transform: translateY(-1.5%); }
}
@keyframes ibgDrop {
  0% { transform: translateY(-330%) scale(0.85); opacity: 0; }
  12% { opacity: 1; }
  58% { transform: translateY(0) scale(1); }
  72% { transform: translateY(-14%); }
  84% { transform: translateY(0); }
  92% { transform: translateY(-5%); }
  100% { transform: translateY(0); }
}
@keyframes ibgPulse {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-4%) scale(1.05); }
}
@keyframes ibgNoteIn {
  0% { opacity: 0; transform: rotate(6deg) scale(0.55) translateY(60px); }
  100% { opacity: 1; transform: rotate(-1.2deg) scale(1) translateY(0); }
}
@keyframes ibgPanelIn {
  0% { opacity: 0; transform: scale(0.85) translateY(40px); }
  100% { opacity: 1; transform: none; }
}
@keyframes ibgFlyFold {
  0% { transform: translate(0, 0) scale(1) rotate(-2deg); opacity: 1; }
  30% { transform: translate(5vw, -5vh) scale(0.72) rotate(8deg); opacity: 1; }
  100% { transform: translate(calc(50vw - 130px), calc(50vh - 120px)) scale(0.08) rotate(22deg); opacity: 0.9; }
}
@keyframes ibgFadeUp {
  0% { opacity: 0; transform: translateY(18px); }
  100% { opacity: 1; transform: none; }
}
@keyframes ibgBadgePop {
  0% { transform: scale(1); }
  40% { transform: scale(1.5); }
  100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
  .ibg-root *, .ibg-overlay * { animation-duration: 0.01s !important; transition-duration: 0.01s !important; }
}

.ibg-btn-primary {
  border: none; cursor: pointer;
  background: var(--c-accent); color: var(--c-n-100);
  font-family: var(--f-body); font-weight: 700; letter-spacing: 0.03em;
  border-radius: 999px;
  box-shadow: 0 12px 28px rgba(86, 99, 63, 0.35);
  transition: transform 0.25s cubic-bezier(0.2, 1, 0.3, 1), background 0.2s, opacity 0.3s;
}
.ibg-btn-primary:hover { transform: translateY(-2px); background: var(--c-accent-600); }
.ibg-btn-primary:active { transform: scale(0.97); }

.ibg-btn-warm {
  border: none; cursor: pointer;
  background: var(--c-warm); color: var(--c-n-100);
  font-family: var(--f-body); font-weight: 700;
  border-radius: 999px;
  transition: transform 0.2s, background 0.2s;
}
.ibg-btn-warm:hover { background: var(--c-warm-600); }
.ibg-btn-warm:active { transform: scale(0.96); }

.ibg-btn-outline {
  border: 1.5px solid var(--c-accent-400); cursor: pointer;
  background: transparent; color: var(--c-accent-700);
  font-family: var(--f-body); font-weight: 700;
  border-radius: 999px;
  transition: background 0.2s, transform 0.2s;
}
.ibg-btn-outline:hover { background: var(--c-accent-100); }
.ibg-btn-outline:active { transform: scale(0.96); }

.ibg-input {
  border: 1.5px solid var(--c-n-400); background: var(--c-n-100);
  border-radius: 999px; padding: 14px 22px;
  font-family: var(--f-body); font-size: 16px; color: var(--c-text);
}
.ibg-input:focus { border-color: var(--c-accent); box-shadow: 0 0 0 4px var(--c-accent-200); }

.ibg-chip-x {
  border: none; cursor: pointer; width: 24px; height: 24px; border-radius: 50%;
  background: var(--c-warm-300); color: var(--c-warm-800);
  font-size: 13px; line-height: 1;
  display: flex; align-items: center; justify-content: center;
}
.ibg-chip-x:hover { background: var(--c-warm-400); }

.ibg-close {
  border: none; cursor: pointer; border-radius: 50%;
  background: var(--c-n-200); color: var(--c-n-700);
}
.ibg-close:hover { background: var(--c-n-300); }

.ibg-rail-item { cursor: pointer; border-radius: 999px; padding: 2px 6px; transition: background 0.2s; }
.ibg-rail-item:hover { background: var(--c-accent-100); }

.ibg-crank-hit { cursor: pointer; transition: transform 0.2s cubic-bezier(0.2, 1, 0.3, 1); }
.ibg-crank-hit:hover { transform: scale(1.04); }
.ibg-crank-hit:active { transform: scale(0.96); }

.ibg-capsule-ball { transition: transform 0.2s; }
.ibg-capsule:hover .ibg-capsule-ball { transform: scale(1.08); }

.ibg-history-fab { cursor: pointer; transition: transform 0.25s cubic-bezier(0.2, 1, 0.3, 1); }
.ibg-history-fab:hover { transform: translateY(-3px); }

.ibg-person-row {
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.2, 1, 0.3, 1), background 0.2s;
}
.ibg-person-row:hover { transform: translateX(4px); background: var(--c-accent-100); }

.ibg-ghost-link {
  border: none; cursor: pointer; background: transparent;
  color: var(--c-accent-700); font-family: var(--f-body); font-weight: 600;
  text-decoration: underline; text-underline-offset: 3px;
}
.ibg-ghost-link:hover { color: var(--c-accent-600); }
`;

export default GACHA_CSS;
